/**
 * Medplum-backed CRUD for practice sites (Location bu), rooms (Location ro), and equipment (Device).
 * @see docs/prd/LocationsRoomsEquipment/PRD_LOCATIONS_ROOMS_EQUIPMENT_FHIR_MAPPING.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Device, Location } from '@medplum/fhirtypes';

import {
  buildPracticeEquipmentDevice,
  getDeviceDisplayName,
  getDevicePortability,
  setDevicePortability,
} from '@unify/fhir-model/resources/device';
import {
  deleteEquipment as deleteEquipmentBot,
  deleteRoom as deleteRoomBot,
  deleteSite as deleteSiteBot,
} from '../lib/locationManagementService';
import {
  buildPracticeRoomLocation,
  buildPracticeSiteLocation,
  applyClampedCustomHoursToRoom,
  getAdaAccessible,
  getLocationWeeklyHours,
  getLocationWorkPhone,
  getRoomCapacity,
  getRoomTypeCode,
  isPracticeSiteLocation,
  isRoomLocation,
  parseLocationRefId,
  roomHasCustomHours,
  type PracticeRoomLocationInput,
  type PracticeSiteLocationInput,
} from '@unify/fhir-model/resources/location';
import type { Equipment, Portability } from '../pages/settings/components/EquipmentManagementSection';
import type { Room } from '../pages/settings/components/RoomManagementSection';
import type { PracticeLocation } from '../pages/users/types/rbac';

/** Clamp custom hours to the parent site at write time; inherited rooms are unchanged. */
export function roomResourceForSave(draft: Location, site: Location): Location {
  return applyClampedCustomHoursToRoom(draft, getLocationWeeklyHours(site));
}

function managingOrgId(loc: Location): string | undefined {
  const ref = loc.managingOrganization?.reference;
  if (!ref?.startsWith('Organization/')) return undefined;
  return ref.slice('Organization/'.length);
}

function siteToPracticeLocation(loc: Location): PracticeLocation {
  const line = loc.address?.line ?? [];
  return {
    id: loc.id ?? '',
    name: loc.name ?? '',
    addressLine1: line[0] ?? '',
    addressLine2: line[1],
    city: loc.address?.city ?? '',
    state: loc.address?.state ?? '',
    zipCode: loc.address?.postalCode ?? '',
    phone: getLocationWorkPhone(loc),
    latitude: loc.position?.latitude,
    longitude: loc.position?.longitude,
    status: loc.status === 'inactive' ? 'inactive' : 'active',
    hours: getLocationWeeklyHours(loc),
  };
}

function roomToUiRoom(loc: Location): Room {
  const rt = getRoomTypeCode(loc);
  return {
    id: loc.id ?? '',
    name: loc.name ?? '',
    type: rt === 'phone-booth' ? 'phone-booth' : 'therapy-room',
    capacity: getRoomCapacity(loc) ?? 1,
    adaAccessible: getAdaAccessible(loc) ?? false,
    status: loc.status === 'inactive' ? 'inactive' : 'active',
    hours: getLocationWeeklyHours(loc),
  };
}

function deviceToEquipment(dev: Device): Equipment {
  const portability = (getDevicePortability(dev) ?? 'portable') as Portability;
  const locId = parseLocationRefId(dev.location) ?? '';
  return {
    id: dev.id ?? '',
    name: getDeviceDisplayName(dev) ?? '',
    portability,
    homeRoomId: portability === 'fixed' ? locId : undefined,
    status: dev.status === 'inactive' ? 'inactive' : 'active',
  };
}

function roomParentSiteId(room: Location): string | undefined {
  return parseLocationRefId(room.partOf);
}

/** Site id that should own this device in the practice-settings UI grouping. */
function deviceOwningSiteId(
  dev: Device,
  siteIds: Set<string>,
  roomToSite: Map<string, string>,
): string | undefined {
  const locId = parseLocationRefId(dev.location);
  if (!locId) return undefined;
  if (siteIds.has(locId)) return locId;
  const site = roomToSite.get(locId);
  return site;
}

function buildPlanningHorizon(): { start: string; end: string } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 2);
  return { start: start.toISOString(), end: end.toISOString() };
}

async function ensureScheduleForActor(
  medplum: ReturnType<typeof useMedplum>,
  actorReference: string,
): Promise<void> {
  const existing = await medplum.searchResources('Schedule', { actor: actorReference, _count: '10' });
  if (existing.length > 0) return;
  await medplum.createResource({
    resourceType: 'Schedule',
    active: true,
    actor: [{ reference: actorReference }],
    planningHorizon: buildPlanningHorizon(),
  });
}

async function deleteSchedulesForActor(
  medplum: ReturnType<typeof useMedplum>,
  actorReference: string,
): Promise<void> {
  const schedules = await medplum.searchResources('Schedule', { actor: actorReference, _count: '50' });
  for (const schedule of schedules) {
    if (!schedule.id) continue;
    const slots = await medplum.searchResources('Slot', {
      schedule: `Schedule/${schedule.id}`,
      _count: '500',
    });
    for (const slot of slots) {
      if (!slot.id) continue;
      await medplum.deleteResource('Slot', slot.id);
    }
    await medplum.deleteResource('Schedule', schedule.id);
  }
}

export interface UseLocationResourcesOptions {
  organizationId: string;
  /** Used on FHIR references (display). */
  organizationDisplay?: string;
  /**
   * Load sites/rooms/equipment for every practice instead of just `organizationId`.
   * Unify admins work across practices, so scoping to their own organization (often
   * none) would leave them with no room or equipment options on another practice's
   * appointment. Read-only — the create/update helpers still write to `organizationId`.
   */
  crossOrganization?: boolean;
}

export interface GroupedLocationResources {
  sites: PracticeLocation[];
  roomsBySiteId: Record<string, Room[]>;
  equipmentBySiteId: Record<string, Equipment[]>;
}

/** Bucket Locations and Devices into sites, rooms per site, and equipment per site. */
export function groupLocationResources(
  locations: Location[],
  devices: Device[],
  opts: { organizationId: string; crossOrganization?: boolean },
): GroupedLocationResources {
  const scoped = opts.crossOrganization
    ? locations
    : locations.filter((l) => managingOrgId(l) === opts.organizationId);
  const sitesFhir = scoped.filter(isPracticeSiteLocation);
  const roomsFhir = scoped.filter(isRoomLocation);
  const siteIds = new Set(sitesFhir.map((s) => s.id).filter(Boolean) as string[]);

  const roomToSite = new Map<string, string>();
  for (const r of roomsFhir) {
    const sid = roomParentSiteId(r);
    if (r.id && sid) roomToSite.set(r.id, sid);
  }

  const roomsBySiteId: Record<string, Room[]> = {};
  for (const sid of siteIds) {
    roomsBySiteId[sid] = [];
  }
  for (const r of roomsFhir) {
    const sid = roomParentSiteId(r);
    if (!sid || !r.id) continue;
    if (!roomsBySiteId[sid]) roomsBySiteId[sid] = [];
    roomsBySiteId[sid].push(roomToUiRoom(r));
  }

  const equipmentBySiteId: Record<string, Equipment[]> = {};
  for (const sid of siteIds) {
    equipmentBySiteId[sid] = [];
  }
  for (const d of devices) {
    if (!d.id) continue;
    const sid = deviceOwningSiteId(d, siteIds, roomToSite);
    if (!sid) continue;
    if (!equipmentBySiteId[sid]) equipmentBySiteId[sid] = [];
    equipmentBySiteId[sid].push(deviceToEquipment(d));
  }

  return { sites: sitesFhir.map(siteToPracticeLocation), roomsBySiteId, equipmentBySiteId };
}

export interface UseLocationResourcesResult {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  sites: PracticeLocation[];
  roomsBySiteId: Record<string, Room[]>;
  equipmentBySiteId: Record<string, Equipment[]>;
  createSite: (input: PracticeSiteLocationInput) => Promise<void>;
  updateSite: (siteId: string, input: PracticeSiteLocationInput) => Promise<void>;
  deleteSite: (siteId: string) => Promise<void>;
  createRoom: (siteId: string, input: PracticeRoomLocationInput) => Promise<void>;
  updateRoom: (siteId: string, roomId: string, input: PracticeRoomLocationInput) => Promise<void>;
  deleteRoom: (roomId: string) => Promise<void>;
  createEquipment: (
    siteId: string,
    input: {
      name: string;
      portability: Portability;
      homeRoomId?: string;
      status: Equipment['status'];
    },
  ) => Promise<void>;
  updateEquipment: (
    siteId: string,
    equipmentId: string,
    input: {
      name: string;
      portability: Portability;
      homeRoomId?: string;
      status: Equipment['status'];
    },
  ) => Promise<void>;
  deleteEquipment: (equipmentId: string) => Promise<void>;
}

async function searchLocationsForOrg(
  medplum: ReturnType<typeof useMedplum>,
  organizationId: string | undefined,
): Promise<Location[]> {
  try {
    return await medplum.searchResources('Location', {
      ...(organizationId ? { organization: `Organization/${organizationId}` } : {}),
      _count: '500',
    });
  } catch {
    return [];
  }
}

async function searchDevicesForOrg(
  medplum: ReturnType<typeof useMedplum>,
  organizationId: string | undefined,
): Promise<Device[]> {
  try {
    return await medplum.searchResources('Device', {
      ...(organizationId ? { organization: `Organization/${organizationId}` } : {}),
      _count: '500',
    });
  } catch {
    return [];
  }
}

export function useLocationResources(opts: UseLocationResourcesOptions): UseLocationResourcesResult {
  const { organizationId, organizationDisplay, crossOrganization = false } = opts;
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<PracticeLocation[]>([]);
  const [roomsBySiteId, setRoomsBySiteId] = useState<Record<string, Room[]>>({});
  const [equipmentBySiteId, setEquipmentBySiteId] = useState<Record<string, Equipment[]>>({});

  const orgRef = useMemo(() => organizationId, [organizationId]);

  const recomputeFromResources = useCallback((locList: Location[], devList: Device[]) => {
    const grouped = groupLocationResources(locList, devList, {
      organizationId: orgRef,
      crossOrganization,
    });
    setSites(grouped.sites);
    setRoomsBySiteId(grouped.roomsBySiteId);
    setEquipmentBySiteId(grouped.equipmentBySiteId);
  }, [orgRef, crossOrganization]);

  const refetch = useCallback(async () => {
    if (!orgRef && !crossOrganization) {
      setSites([]);
      setRoomsBySiteId({});
      setEquipmentBySiteId({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      medplum.invalidateSearches('Location');
      medplum.invalidateSearches('Device');
      medplum.invalidateSearches('Schedule');
      medplum.invalidateSearches('Slot');
      const searchOrgId = crossOrganization ? undefined : orgRef;
      const [locList, devList] = await Promise.all([
        searchLocationsForOrg(medplum, searchOrgId),
        searchDevicesForOrg(medplum, searchOrgId),
      ]);
      recomputeFromResources(locList, devList);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [medplum, orgRef, crossOrganization, recomputeFromResources]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const createSite = useCallback(
    async (input: PracticeSiteLocationInput) => {
      const draft = buildPracticeSiteLocation(orgRef, organizationDisplay, input, undefined);
      delete (draft as { id?: string }).id;
      await medplum.createResource(draft);
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const updateSite = useCallback(
    async (siteId: string, input: PracticeSiteLocationInput) => {
      const existing = await medplum.readResource('Location', siteId);
      const updated = buildPracticeSiteLocation(orgRef, organizationDisplay, input, existing);
      await medplum.updateResource(updated);
      const children = await medplum.searchResources('Location', {
        partof: `Location/${siteId}`,
        _count: '200',
      });
      for (const child of children) {
        if (!roomHasCustomHours(child)) continue;
        const clamped = applyClampedCustomHoursToRoom(child, input.hours);
        if (
          clamped.status !== child.status
          || JSON.stringify(clamped.hoursOfOperation) !== JSON.stringify(child.hoursOfOperation)
        ) {
          await medplum.updateResource(clamped);
        }
      }
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const deleteSite = useCallback(
    async (siteId: string) => {
      await deleteSiteBot(medplum, siteId);
      await refetch();
    },
    [medplum, refetch],
  );

  const createRoom = useCallback(
    async (siteId: string, input: PracticeRoomLocationInput) => {
      const site = await medplum.readResource('Location', siteId);
      const draft = roomResourceForSave(
        buildPracticeRoomLocation(
          orgRef,
          organizationDisplay,
          siteId,
          site.name,
          input,
          undefined,
        ),
        site,
      );
      delete (draft as { id?: string }).id;
      const room = await medplum.createResource(draft);
      if (room.id) {
        await ensureScheduleForActor(medplum, `Location/${room.id}`);
      }
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const updateRoom = useCallback(
    async (_siteId: string, roomId: string, input: PracticeRoomLocationInput) => {
      const existing = await medplum.readResource('Location', roomId);
      const siteId = roomParentSiteId(existing);
      if (!siteId) throw new Error('Room has no parent site');
      const site = await medplum.readResource('Location', siteId);
      const updated = roomResourceForSave(
        buildPracticeRoomLocation(
          orgRef,
          organizationDisplay,
          siteId,
          site.name,
          input,
          existing,
        ),
        site,
      );
      await medplum.updateResource(updated);
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const deleteRoom = useCallback(
    async (roomId: string) => {
      await deleteRoomBot(medplum, roomId);
      await refetch();
    },
    [medplum, refetch],
  );

  const createEquipment = useCallback(
    async (
      siteId: string,
      input: {
        name: string;
        portability: Portability;
        homeRoomId?: string;
        status: Equipment['status'];
      },
    ) => {
      const locationId = input.portability === 'fixed' ? (input.homeRoomId ?? '') : siteId;
      const loc = await medplum.readResource('Location', locationId);
      const draft = buildPracticeEquipmentDevice(
        orgRef,
        organizationDisplay,
        {
          name: input.name,
          portability: input.portability,
          locationId,
          locationDisplay: loc.name,
          status: input.status === 'active' ? 'active' : 'inactive',
        },
        undefined,
      );
      delete (draft as { id?: string }).id;
      const device = await medplum.createResource(draft);
      if (input.portability === 'portable' && device.id) {
        await ensureScheduleForActor(medplum, `Device/${device.id}`);
      }
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const updateEquipment = useCallback(
    async (
      _siteId: string,
      equipmentId: string,
      input: {
        name: string;
        portability: Portability;
        homeRoomId?: string;
        status: Equipment['status'];
      },
    ) => {
      const existing = await medplum.readResource('Device', equipmentId);
      const locationId = input.portability === 'fixed' ? (input.homeRoomId ?? '') : _siteId;
      const loc = await medplum.readResource('Location', locationId);
      let updated = buildPracticeEquipmentDevice(
        orgRef,
        organizationDisplay,
        {
          name: input.name,
          portability: input.portability,
          locationId,
          locationDisplay: loc.name,
          status: input.status === 'active' ? 'active' : 'inactive',
        },
        existing,
      );
      updated = setDevicePortability(updated, input.portability);
      await medplum.updateResource(updated);
      if (updated.id) {
        if (input.portability === 'portable') {
          await ensureScheduleForActor(medplum, `Device/${updated.id}`);
        } else {
          await deleteSchedulesForActor(medplum, `Device/${updated.id}`);
        }
      }
      await refetch();
    },
    [medplum, orgRef, organizationDisplay, refetch],
  );

  const deleteEquipment = useCallback(
    async (equipmentId: string) => {
      await deleteEquipmentBot(medplum, equipmentId);
      await refetch();
    },
    [medplum, refetch],
  );

  return {
    loading,
    error,
    refetch,
    sites,
    roomsBySiteId,
    equipmentBySiteId,
    createSite,
    updateSite,
    deleteSite,
    createRoom,
    updateRoom,
    deleteRoom,
    createEquipment,
    updateEquipment,
    deleteEquipment,
  };
}
